import { lazy, Suspense } from 'react';
import { BrowserRouter, matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { ModalProvider } from './hooks/useModals';
import { CollectionProvider } from './hooks/useCollection';
import { SET_SLUG_TO_CODE } from './constants';
import { AppLayout } from './components/layout/AppLayout';
import { RouteSuspenseFallback } from './components/layout/pageLoadingSkeletons';
import { HomePage } from './views/HomePage';
import { SearchSidebarBound } from './views/SearchSidebarBound';
import { CollectionSidebarBound } from './views/CollectionSidebarBound';
import { MatchHistorySidebarBound } from './views/MatchHistorySidebarBound';
import { LegacyCardRedirect } from './views/LegacyCardRedirect';
import { CardPrintProvider } from './views/card-print/CardPrintContext';
import { CardPrintSidebarBound } from './views/CardPrintSidebarBound';
import { GoogleAnalytics } from './analytics/GoogleAnalytics';
import { PwaInstallBanner } from './components/PwaInstallBanner';
import { PwaAndroidRepairBanner } from './components/PwaAndroidRepairBanner';
import { DevControlsProvider } from './components/dev/DevControlsContext';
import { DevControlsHost } from './components/dev/DevControlsHost';
import { MobileTabKeepAlive } from './components/layout/MobileTabKeepAlive';
import { CollectionScanModalLayer } from './components/scan/CollectionScanModalLayer';
import { NewUserProfilePromptHost } from './components/profile/NewUserProfilePrompt';
import { GuestScanAccountPromptHost } from './components/scan/GuestScanAccountPrompt';
import { TeamLifecycleEffects } from './features/teams/components/TeamLifecycleEffects';
import { getScanModalBackground } from './scan/scanModalNavigation';
const NotFoundPage = lazy(() =>
  import('./views/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const SearchPage = lazy(() =>
  import('./views/SearchPage').then((m) => ({ default: m.SearchPage }))
);
const CollectionPage = lazy(() =>
  import('./views/CollectionPage').then((m) => ({ default: m.CollectionPage }))
);
const CollectionScanPage = lazy(() =>
  import('./views/CollectionScanPage').then((m) => ({ default: m.CollectionScanPage }))
);
const BinderPage = lazy(() =>
  import('./views/BinderPage').then((m) => ({ default: m.BinderPage }))
);
const BindersPage = lazy(() =>
  import('./views/BindersPage').then((m) => ({ default: m.BindersPage }))
);
const CardPrintPage = lazy(() =>
  import('./views/CardPrintPage').then((m) => ({ default: m.CardPrintPage }))
);
const DeckBuilderPage = lazy(() =>
  import('./views/DeckBuilderPage').then((m) => ({ default: m.DeckBuilderPage }))
);
const DecksPage = lazy(() =>
  import('./views/DecksPage').then((m) => ({ default: m.DecksPage }))
);
const DeckDetailPage = lazy(() =>
  import('./views/DeckDetailPage').then((m) => ({ default: m.DeckDetailPage }))
);
const ProfilePage = lazy(() =>
  import('./views/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const AboutPage = lazy(() =>
  import('./views/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const PrivacyPage = lazy(() =>
  import('./views/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() =>
  import('./views/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const PublicCollectionPage = lazy(() =>
  import('./views/PublicCollectionPage').then((m) => ({ default: m.PublicCollectionPage }))
);
const PublicProfilePage = lazy(() =>
  import('./views/PublicProfilePage').then((m) => ({ default: m.PublicProfilePage }))
);
const SetsPage = lazy(() =>
  import('./views/SetsPage').then((m) => ({ default: m.SetsPage }))
);
const SetDetailPage = lazy(() =>
  import('./views/SetDetailPage').then((m) => ({ default: m.SetDetailPage }))
);
const LegendsPage = lazy(() =>
  import('./views/LegendsPage').then((m) => ({ default: m.LegendsPage }))
);
const ErrataPage = lazy(() =>
  import('./views/ErrataPage').then((m) => ({ default: m.ErrataPage }))
);
const LegendDetailPage = lazy(() =>
  import('./views/LegendDetailPage').then((m) => ({ default: m.LegendDetailPage }))
);
const RiftboundlePage = lazy(() =>
  import('./views/RiftboundlePage').then((m) => ({ default: m.RiftboundlePage }))
);
const PlayHomePage = lazy(() =>
  import('./views/PlayHomePage').then((m) => ({ default: m.PlayHomePage }))
);
const PlayPage = lazy(() =>
  import('./views/PlayPage').then((m) => ({ default: m.PlayPage }))
);
const MatchJoinPage = lazy(() =>
  import('./views/MatchJoinPage').then((m) => ({ default: m.MatchJoinPage }))
);
const MatchSessionPage = lazy(() =>
  import('./views/MatchSessionPage').then((m) => ({ default: m.MatchSessionPage }))
);
const MatchHistoryPage = lazy(() =>
  import('./views/MatchHistoryPage').then((m) => ({ default: m.MatchHistoryPage }))
);
const EventsPage = lazy(() =>
  import('./views/EventsPage').then((m) => ({ default: m.EventsPage }))
);
const EventRecapPage = lazy(() =>
  import('./views/EventRecapPage').then((m) => ({ default: m.EventRecapPage }))
);
const MatchResultDetailPage = lazy(() =>
  import('./views/MatchResultDetailPage').then((m) => ({ default: m.MatchResultDetailPage }))
);
const DeckPerformancePage = lazy(() =>
  import('./views/DeckPerformancePage').then((m) => ({ default: m.DeckPerformancePage }))
);
const MatchTrackerFeaturePage = lazy(() =>
  import('./views/MatchTrackerFeaturePage').then((m) => ({ default: m.MatchTrackerFeaturePage }))
);
const CollectionFeaturePage = lazy(() =>
  import('./views/CollectionFeaturePage').then((m) => ({ default: m.CollectionFeaturePage }))
);
const TeamFeaturePage = lazy(() =>
  import('./views/TeamFeaturePage').then((m) => ({ default: m.TeamFeaturePage }))
);
const TeamLandingPage = lazy(() =>
  import('./features/teams/pages/TeamLandingPage').then((m) => ({ default: m.TeamLandingPage }))
);
const TeamProfilePage = lazy(() =>
  import('./features/teams/pages/TeamProfilePage').then((m) => ({ default: m.TeamProfilePage }))
);

/**
 * Card-print gets its own shell so that `CardPrintProvider` wraps both
 * the sidebar (`CardPrintSidebarBound`) and the page content (`CardPrintPage`)
 * — they must share the same context instance.
 */
function CardPrintShell() {
  return (
    <CardPrintProvider>
      <AppLayout sidebar={<CardPrintSidebarBound />}>
        <Suspense fallback={<RouteSuspenseFallback />}>
          <Routes>
            <Route path="/card-print" element={<CardPrintPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </CardPrintProvider>
  );
}

function RoutedShell() {
  const location = useLocation();
  const isCardPrint = location.pathname === '/card-print';

  if (isCardPrint) return <><GoogleAnalytics /><CardPrintShell /></>;

  // When scan is a fullScreen modal, keep underlay chrome/sidebar on the background route.
  const shellLocation = getScanModalBackground(location) ?? location;

  const setDetailMatch = matchPath('/sets/:slug', shellLocation.pathname);
  const setDetailCode = setDetailMatch?.params.slug
    ? SET_SLUG_TO_CODE[setDetailMatch.params.slug]?.toUpperCase()
    : undefined;
  const showSearchSidebar =
    shellLocation.pathname === '/search' ||
    shellLocation.pathname.startsWith('/cards') ||
    shellLocation.pathname === '/errata' ||
    Boolean(setDetailCode);
  // Sidebar for /collection and set-stats deep links, not scan/binders/etc.
  const showCollectionSidebar =
    shellLocation.pathname === '/collection' ||
    Boolean(matchPath('/collection/sets/:setSlug', shellLocation.pathname));
  const showMatchHistorySidebar = shellLocation.pathname === '/matches';

  const sidebar = showMatchHistorySidebar
    ? <MatchHistorySidebarBound />
    : showCollectionSidebar
    ? <CollectionSidebarBound />
    : setDetailCode
      ? <SearchSidebarBound fixedSetCode={setDetailCode} />
    : showSearchSidebar
      ? <SearchSidebarBound />
      : undefined;

  return (
    <AppLayout sidebar={sidebar}>
      <GoogleAnalytics />
      <MobileTabKeepAlive>
        {(routeLocation) => (
          <Suspense fallback={<RouteSuspenseFallback />}>
            <Routes location={routeLocation}>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              {/* Human-readable card detail: /cards/{setCode}/{number}-{name-slug} */}
              <Route path="/cards/:setCode/:cardSlug" element={<SearchPage />} />
              {/* Legacy opaque-ID URLs redirect to the slug form */}
              <Route path="/cards/:legacyId" element={<LegacyCardRedirect />} />
              {/* Nested set-stats so opening the sheet does not remount CollectionPage. */}
              <Route path="/collection" element={<CollectionPage />}>
                <Route path="sets/:setSlug" />
              </Route>
              <Route path="/collection/binders" element={<BindersPage />} />
              <Route path="/collection/binders/:binderId" element={<BinderPage />} />
              {/* Deep link / refresh: no modal background — render inline. */}
              <Route path="/collection/scan" element={<CollectionScanPage />} />
              <Route path="/binders" element={<Navigate to="/collection/binders" replace />} />
              <Route path="/binders/:binderId" element={<Navigate to={`/collection/binders/${location.pathname.split('/').pop() ?? ''}`} replace />} />
              {/* Nested set-stats so the public sheet does not remount the page. */}
              <Route path="/collections/:slug" element={<PublicCollectionPage />}>
                <Route path="sets/:setSlug" />
              </Route>
              <Route path="/players/:slug" element={<PublicProfilePage />} />

              {/* Deck builder + gallery — open to everyone */}
              <Route path="/decks/new" element={<DeckBuilderPage />} />
              <Route path="/decks/mine" element={<Navigate to="/decks/my-decks" replace />} />
              <Route path="/decks/:deckId/edit" element={<DeckBuilderPage />} />
              <Route path="/decks/:deckId/versions" element={<DeckDetailPage />} />
              <Route path="/decks/:deckId/performance" element={<DeckPerformancePage />} />
              <Route path="/decks/:deckId" element={<DeckDetailPage />} />
              {/* Nested my-decks so Community ↔ My Decks does not remount DecksPage. */}
              <Route path="/decks" element={<DecksPage />}>
                <Route path="my-decks" />
              </Route>
              <Route path="/deck-builder" element={<Navigate to="/decks/new" replace />} />

              {/* Sets + Legends browse pages */}
              <Route path="/sets" element={<SetsPage />} />
              <Route path="/sets/:slug" element={<SetDetailPage />} />
              <Route path="/legends" element={<LegendsPage />} />
              <Route path="/legends/:slug" element={<LegendDetailPage />} />
              <Route path="/errata" element={<ErrataPage />} />
              <Route path="/riftboundle" element={<RiftboundlePage />} />

              {/* In-store play and match history */}
              <Route path="/play" element={<PlayHomePage />} />
              <Route path="/play/match" element={<PlayPage />} />
              <Route path="/play/join" element={<MatchJoinPage />} />
              <Route path="/play/:sessionId" element={<MatchSessionPage />} />
              <Route path="/matches" element={<MatchHistoryPage />} />
              <Route path="/matches/:matchResultId" element={<MatchResultDetailPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventRecapPage />} />

              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/:section" element={<ProfilePage />} />
              <Route path="/team" element={<TeamLandingPage />} />
              <Route path="/teams/:slug" element={<TeamProfilePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/features/match-tracker" element={<MatchTrackerFeaturePage />} />
              <Route path="/features/collection" element={<CollectionFeaturePage />} />
              <Route path="/features/teams" element={<TeamFeaturePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        )}
      </MobileTabKeepAlive>
    </AppLayout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TeamLifecycleEffects />
      <DevControlsProvider>
        <ModalProvider>
          <CollectionProvider>
            <BrowserRouter>
              <RoutedShell />
              {/* Outside AppLayout so fixed cover isn't trapped; still under ModalProvider. */}
              <CollectionScanModalLayer />
              <GuestScanAccountPromptHost />
              <NewUserProfilePromptHost />
              <PwaInstallBanner />
              <PwaAndroidRepairBanner />
              <DevControlsHost />
            </BrowserRouter>
          </CollectionProvider>
        </ModalProvider>
      </DevControlsProvider>
    </AuthProvider>
  );
}
